import React, { useState, useCallback } from 'react';
import { Modal } from 'react-native';
import { XStack, View, Paragraph, useTheme, YStack } from 'tamagui';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Button } from '@components/Button/Button';
import PinPosition, { MapCenter } from '@components/PinPosition/PinPosition';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { Ionicons } from '@expo/vector-icons';
import { MODAL_LEFT_ACTION_SIZE } from '@components/PinModal/pinModal.constant';
import {
  CoordinatesPopOver,
  PinModalHeader,
} from '@components/PinModal/pinModal.styled';

export interface PinModalProps {
  /** Whether the pin modal is visible */
  visible: boolean;
  /** Callback when a position is selected with "Select location" button */
  onPin: (latitude: number, longitude: number) => void;
  /** Callback when the modal is closed without selection */
  onClose: () => void;
}

/**
 * PinModal - Cross-platform location selection modal
 *
 * Features:
 * - Full screen modal with map (PinPosition component)
 * - Bottom popover showing current coordinates
 * - "Select location" button to confirm selection
 * - Close button to dismiss without selection
 */
export default function PinModal({
  visible,
  onPin,
  onClose,
}: PinModalProps): React.ReactElement | null {
  const { t } = useAppTranslation();
  const theme = useTheme();
  const { top, bottom } = useSafeAreaInsets();
  const [mapCenter, setMapCenter] = useState<MapCenter>({
    latitude: 0,
    longitude: 0,
  });

  // Handle position change from PinPosition component
  const handlePositionChange = (position: MapCenter): void => {
    setMapCenter(position);
  };

  // Handle select location button press
  const handleSelectLocation = useCallback(() => {
    onPin(mapCenter.latitude, mapCenter.longitude);
    onClose();
  }, [mapCenter, onPin, onClose]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
    >
      <View flex={1} position="relative" width="100%" zIndex={99}>
        {/* Close button */}
        <PinModalHeader paddingTop={top || '$2'}>
          <Button
            variant="ghost"
            size={MODAL_LEFT_ACTION_SIZE}
            icon={<Ionicons name="close" color={theme.color.val} size={24} />}
            onPress={onClose}
          />
          <Paragraph
            flex={1}
            textAlign="center"
            marginRight={MODAL_LEFT_ACTION_SIZE}
          >
            {t('pinPosition.modalTitle')}
          </Paragraph>
        </PinModalHeader>

        {/* PinPosition map component*/}
        <PinPosition onPositionChange={handlePositionChange} />

        {/* Bottom popover with coordinates and select button */}
        <CoordinatesPopOver paddingBottom={bottom + 20}>
          <YStack gap="$1">
            <Paragraph color="secondary75">
              {t('pinPosition.coordinatesTitle')}
            </Paragraph>
            {/* Coordinates display */}
            <XStack backgroundColor="$tertiary50" padding="$1">
              <Paragraph>
                {mapCenter.latitude.toFixed(4)},{' '}
                {mapCenter.longitude.toFixed(4)}
              </Paragraph>
            </XStack>
          </YStack>

          {/* Select location button */}
          <Button
            width="100%"
            title={t('pinPosition.selectLocation')}
            onPress={handleSelectLocation}
          />
        </CoordinatesPopOver>
      </View>
    </Modal>
  );
}
